import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ROUTES } from '../../../constants/routes.constants'
import { useAuth, type LoginResult } from '../../../context/AuthContext'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { StorefrontBackdrop } from '../../components/storefront/chrome'
import { Reveal } from '../../motion'

/**
 * `/login` — single entry for every user.
 *
 * Calls AuthContext.login() which authenticates against iam_users and
 * returns `{ user, landingPath }` (no `kind` discriminator — one
 * identity for everyone, 2026-05-23). The hook hydrates the user; this
 * page navigates to the server-resolved landing path (operators under
 * /dashboard, customers to /account, per user_types.landing_path).
 *
 * Replaces the previous /admin/login + /account/login pair. Those URLs
 * now redirect here.
 */
export function UnifiedLoginPage(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // If the refresh probe already hydrated an identity, bounce straight
  // to the user's landing path.
  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <Spinner size="lg" />
      </div>
    )
  }
  if (auth.isAuthenticated && auth.user !== null) {
    return <Navigate to={auth.user.landingPath || ROUTES.ACCOUNT} replace />
  }

  function resolveLanding(data: LoginResult): string {
    const fromState = (location.state as { from?: unknown } | null)?.from
    // Defensive: only honor a relative path starting with a single `/`.
    const safeFrom =
      typeof fromState === 'string' && /^\/(?!\/)/.test(fromState) ? fromState : null
    if (safeFrom === null) return data.landingPath

    // Operator: only return into the dashboard tree. Customer logins
    // ALWAYS land at the configured landing_path (today: /shop for every
    // seeded customer) so the post-login state is consistent — Build is
    // reached via the nav button after landing on /shop, not via a
    // post-login redirect. Kiran 2026-06-06.
    if (data.landingPath.startsWith('/dashboard')) {
      return safeFrom.startsWith('/dashboard') ? safeFrom : data.landingPath
    }
    return data.landingPath
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSubmitError(null)
    setSubmitting(true)
    try {
      const data = await auth.login(email, password)
      // The user was already hydrated inside AuthContext.login.
      navigate(resolveLanding(data), { replace: true })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Sign-in failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // Top-aligned + min-h-[100dvh] + block flow so the Sign In button
    // is always reachable on mobile. Same fix as AuthLayout —
    // flex-center on a viewport-tall parent clips the bottom of the
    // card on phones. See SOP 02 §"Mobile Page Scroll".
    <div className="relative min-h-[100dvh] overflow-x-hidden px-4 py-8 sm:py-12">
      <Helmet>
        <title>Sign in — Uyyamadilu</title>
      </Helmet>
      <div className="fixed inset-0 z-0">
        <StorefrontBackdrop />
      </div>
      <Reveal as="div" amount={0.01} duration={1.0} travel={32} className="relative z-10 w-full max-w-md mx-auto bg-bg-primary/95 rounded-2xl border border-border p-6 sm:p-8 shadow-lg backdrop-blur-sm">
        <p className="font-kannada text-h2 text-forest leading-none mb-2">
          ಉಯ್ಯಮಡಿಲು
        </p>
        <h1 className="font-heading text-h2 text-charcoal mb-6">Sign in</h1>

        {submitError !== null ? (
          <div className="mb-4">
            <Alert tone="error">{submitError}</Alert>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">Email</span>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">Password</span>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="submit" variant="primary" size="lg" disabled={submitting} className="flex-1">
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
            <Link to={ROUTES.HOME} className="flex-1">
              <Button type="button" variant="ghost" size="lg" className="w-full">
                Cancel
              </Button>
            </Link>
          </div>
        </form>

        <div className="mt-6 flex flex-col sm:flex-row sm:justify-between gap-2 text-body-sm">
          {/* "Forgot password?" returns in Phase 3.2 alongside the
              transactional email integration — SOP 15 Ch.15 (no
              placeholder routes). */}
          <p className="text-text-secondary sm:ml-auto">
            New to Uyyamadilu?{' '}
            <Link to={ROUTES.ACCOUNTREGISTER} className="text-forest underline-offset-2 hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </Reveal>
    </div>
  )
}
